"use client";

import { useRef, useState, useEffect, useContext } from "react";
import { createPortal } from "react-dom";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { useLoading } from "@/lib/loading-context";
import { usePathname, useSearchParams } from "next/navigation";
import { AuthContext } from "@/lib/auth/context";

// Register GSAP plugins
gsap.registerPlugin(useGSAP);

export function LoadingOverlay() {
  const { isLoading, hideLoading } = useLoading();
  // Use context directly to avoid throwing when outside AuthProvider during SSR/hydration
  const authContext = useContext(AuthContext);
  const user = authContext?.user ?? null;
  const [mounted, setMounted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Handle navigation events to auto-hide loader if it was left open
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    // If user is authenticated, hide immediately on route change
    // Otherwise fall back to a short delay for non-auth route transitions
    if (user) {
      hideLoading();
    } else {
      const timeout = setTimeout(() => {
        hideLoading();
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [pathname, searchParams, hideLoading, user]);

  useGSAP(
    () => {
      if (!svgRef.current) return;

      // Initial setup
      gsap.set(svgRef.current, { visibility: "visible" });
      
      // Random utils
      const random = gsap.utils.random;

      // Setup dots
      gsap.set(".dot", {
        transformOrigin: "50% 50%",
        attr: {
          cx: () => random(350, 450),
          cy: 440,
          r: () => random(4, 20),
        },
      });

      gsap.set(".outsideDot", {
        transformOrigin: "50% 50%",
        attr: {
          cx: () => random(370, 420),
          cy: 420,
          r: () => random(3, 19),
        },
      });

      // Timelines
      const masterTl = gsap.timeline();

      // TL1
      masterTl.add(
        gsap.to(".dots1 .dot", {
          duration: () => random(2, 8),
          attr: { cy: () => random(-220, -320) },
          stagger: { each: 0.16, repeat: -1, repeatRefresh: false },
          ease: "linear",
        }).seek(100),
        0
      );

      // TL2
      masterTl.add(
        gsap.to(".dots2 .dot", {
          duration: () => random(2, 5),
          attr: { cy: () => random(-220, -320) },
          stagger: { each: 0.16, repeat: -1, repeatRefresh: false },
          ease: "sine.in",
        }).seek(100),
        0
      );

      // TL3
      masterTl.add(
        gsap.to(".dots3 .dot", {
          duration: () => random(6, 12),
          attr: { cy: () => random(-220, -320) },
          stagger: { each: 0.16, repeat: -1, repeatRefresh: false },
          ease: "sine.in",
        }).seek(100),
        0
      );

      // TL4
      masterTl.add(
        gsap.to(".dots4 .dot", {
          duration: () => random(3, 9),
          attr: { cy: () => random(-220, -320) },
          stagger: { each: 0.16, repeat: -1, repeatRefresh: false },
          ease: "sine.in",
        }).seek(100),
        0
      );

      // TL5
      masterTl.add(
        gsap.to(".dots5 .outsideDot", {
          duration: () => random(3, 9),
          attr: { cy: () => random(-220, -320), r: 0 },
          stagger: { each: 0.16, repeat: -1, repeatRefresh: false },
          ease: "power2.in",
        }).seek(100),
        0
      );

      // Outline
      masterTl.add(
        gsap.to(".outline", {
          duration: 7, // Simplified wrap logic
          svgOrigin: "400 300",
          rotation: -360,
          ease: "linear",
          repeat: -1,
        }).seek(200),
        0
      );

      return () => {
        masterTl.kill();
      };
    },
    { scope: svgRef, dependencies: [mounted] }
  );

  // Visibility Animation
  useGSAP(
    () => {
      if (isLoading) {
        gsap.to(containerRef.current, {
          opacity: 1,
          duration: 0.5,
          pointerEvents: "auto",
          ease: "power2.out",
        });
      } else {
        gsap.to(containerRef.current, {
          opacity: 0,
          duration: 0.5,
          pointerEvents: "none",
          ease: "power2.in",
        });
      }
    },
    { scope: containerRef, dependencies: [isLoading] }
  );

  if (!mounted) return null;

  return createPortal(
    <div
      ref={containerRef}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-transparent opacity-0 pointer-events-none"
    >
      <div className="w-full max-w-3xl aspect-video transform scale-75 md:scale-100">
        <svg
          ref={svgRef}
          id="mainSVG"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 800 600"
          className="w-full h-full visible"
        >
          <defs>
            <filter id="goo">
              <feGaussianBlur
                in="SourceGraphic"
                stdDeviation="7"
                result="blur"
              />
              <feColorMatrix
                in="blur"
                mode="matrix"
                values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 21 -9"
                result="cm"
              />
              <feBlend />
            </filter>

            {/* Summit Gold Gradient 1 */}
            <linearGradient
              id="grad1"
              gradientUnits="userSpaceOnUse"
              x1="400"
              y1="100"
              x2="400"
              y2="600"
            >
              <stop offset="0" style={{ stopColor: "#B4A089" }} />
              <stop offset="1" style={{ stopColor: "#8B735B" }} />
            </linearGradient>

            {/* Summit Gold Gradient 2 - Darker/Richer */}
            <linearGradient
              id="grad2"
              gradientUnits="userSpaceOnUse"
              x1="400"
              y1="200"
              x2="400"
              y2="400"
            >
              <stop offset="0" style={{ stopColor: "#B4A089" }} />
              <stop offset="1" style={{ stopColor: "#D4CFC4" }} />
            </linearGradient>

            {/* Summit Gold Gradient 3 */}
            <linearGradient
              id="grad3"
              gradientUnits="userSpaceOnUse"
              x1="400"
              y1="500"
              x2="400"
              y2="600"
            >
              <stop offset="0" style={{ stopColor: "#B4A089" }} />
              <stop offset="1" style={{ stopColor: "#8B735B" }} />
            </linearGradient>

            {/* Summit Gold Gradient 4 - Highlight */}
            <linearGradient
              id="grad4"
              gradientUnits="userSpaceOnUse"
              x1="400"
              y1="200"
              x2="400"
              y2="300"
            >
              <stop offset="0" style={{ stopColor: "#F8F6F2" }} />
              <stop offset="1" style={{ stopColor: "#B4A089" }} />
            </linearGradient>

            {/* Summit Gold Gradient 5 */}
            <linearGradient
              id="grad5"
              gradientUnits="userSpaceOnUse"
              x1="400"
              y1="100"
              x2="400"
              y2="600"
            >
              <stop offset="0" style={{ stopColor: "#B4A089" }} />
              <stop offset="1" style={{ stopColor: "#F8F6F2" }} />
            </linearGradient>

            <clipPath id="mainMask">
              <circle cx="400" cy="300" r="100" />
            </clipPath>

            <clipPath id="otherMask">
              <rect x="300" y="0" width="200" height="400" rx="100" ry="100" />
            </clipPath>
          </defs>

          <g>
            <g id="whole" clipPath="url(#mainMask)">
              <g className="dots1" filter="url(#goo)" fill="url(#grad1)">
                <circle
                  className="outline"
                  cx="400"
                  cy="300"
                  r="90"
                  fill="none"
                  stroke="#B4A089"
                  strokeWidth="23"
                  strokeDasharray="160 65"
                />
                {[...Array(9)].map((_, i) => (
                  <circle key={`d1-${i}`} className="dot" cx="0" cy="0" r="20" />
                ))}
              </g>

              <g className="dots2" filter="url(#goo)" fill="url(#grad2)">
                <circle
                  className="outline"
                  cx="400"
                  cy="300"
                  r="90"
                  fill="none"
                  stroke="#8B735B"
                  strokeWidth="23"
                  strokeDasharray="160 40"
                />
                {[...Array(9)].map((_, i) => (
                  <circle key={`d2-${i}`} className="dot" cx="0" cy="0" r="20" />
                ))}
              </g>

              <g className="dots3" filter="url(#goo)" fill="url(#grad3)">
                <circle
                  className="outline"
                  cx="400"
                  cy="300"
                  r="90"
                  fill="none"
                  stroke="url(#grad3)"
                  strokeWidth="23"
                  strokeDasharray="240 "
                />
                {[...Array(9)].map((_, i) => (
                  <circle key={`d3-${i}`} className="dot" cx="0" cy="0" r="20" />
                ))}
              </g>

              <g className="dots4" filter="url(#goo)" fill="url(#grad4)">
                {[...Array(9)].map((_, i) => (
                  <circle key={`d4-${i}`} className="dot" cx="0" cy="0" r="20" />
                ))}
              </g>
            </g>
          </g>

          <g>
            <g clipPath="url(#otherMask)">
              <g className="dots5" fill="url(#grad2)" filter="url(#goo)">
                {[...Array(9)].map((_, i) => (
                  <circle
                    key={`d5-${i}`}
                    className="outsideDot"
                    cx="0"
                    cy="0"
                    r="20"
                  />
                ))}
              </g>
            </g>
          </g>
          
          {/* Copyright/Brand placeholder if needed */}
          
        </svg>
      </div>
    </div>,
    document.body
  );
}
